import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex, Text } from "styles/utility-components";
import { z } from "zod";

import type { McpProvider } from "components/integrations/mcpProviders";
import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import {
  type McpClientWithSecret,
  redirectUriSchema,
  type TokenEndpointAuthMethod,
  toolNameSchema,
} from "lib/hooks/api/auth/mcp/schemas";
import { useCreateMcpClient } from "lib/hooks/api/auth/mcp/useCreateMcpClient";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { ErrorText } from "../sso/SharedStyles";
import { AuthMethodField } from "./AuthMethodField";
import { CallbackUrlField } from "./CallbackUrlField";

/**
 * Which fields apply depends on the platform — the generic tile has no fixed name, and a platform
 * that issues its callback URL later has nothing to type yet — so the schema is built per provider
 * rather than being a fixed shape that leaves the real checks to the submit handler.
 */
function buildFormSchema(needsToolName: boolean, needsRedirectUri: boolean) {
  return z.object({
    toolName: needsToolName ? toolNameSchema : z.string().optional(),
    redirectUri: needsRedirectUri ? redirectUriSchema : z.string().optional(),
  });
}

type FormValues = z.infer<ReturnType<typeof buildFormSchema>>;

type Props = {
  provider: McpProvider;
  onClose: () => void;
  onCreated: (credential: McpClientWithSecret) => void;
};

export function GenerateCredentialModal({ provider, onClose, onCreated }: Props) {
  const currentUser = useAssertCurrentUser();
  const companyName = currentUser.company.name;
  const selfServe = provider.selfServe;
  const needsToolName = !selfServe?.toolName;
  const needsRedirectUri = !selfServe?.callbackUrlIssuedLater;
  // A named platform's method is known, so only the generic tile asks. Either way it can be
  // changed on the credential afterwards.
  const [authMethod, setAuthMethod] = useState<TokenEndpointAuthMethod>(
    selfServe?.tokenEndpointAuthMethod ?? "client_secret_basic",
  );

  const formSchema = useMemo(
    () => buildFormSchema(needsToolName, needsRedirectUri),
    [needsToolName, needsRedirectUri],
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { redirectUri: selfServe?.defaultRedirectUri },
  });

  const { mutate: createClient, isLoading } = useCreateMcpClient({
    onSuccess: (credential) => onCreated(credential),
    onError: (error) => toast.error(error.message ?? "Could not create the credential"),
  });

  // The resolver has already applied whichever of the two schemas applies, so the values arriving
  // here are validated and trimmed.
  function onSubmit(values: FormValues) {
    if (!selfServe || !companyName) return;

    createClient({
      toolName: selfServe.toolName ?? values.toolName ?? "",
      companyName,
      tokenEndpointAuthMethod: authMethod,
      redirectUris: values.redirectUri ? [values.redirectUri] : undefined,
    });
  }

  return (
    <Modal open onOpenChange={onClose} maxWidth="520px" padding={24}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex column gap={24}>
          <Flex column gap={8}>
            <Text h2>Generate credentials</Text>
            <Text description>
              Creates a client ID and secret for connecting {provider.name} to Stotles. The secret
              is shown once.
            </Text>
          </Flex>

          {needsToolName && (
            <Flex column gap={6}>
              <label htmlFor="toolName">Tool name</label>
              <Input
                id="toolName"
                placeholder="e.g. Acme Assistant"
                aria-label="Tool name"
                {...register("toolName")}
              />
              <Text description>
                So you can tell your credentials apart. Your company name is added automatically.
              </Text>
              {errors.toolName && <ErrorText>{errors.toolName.message}</ErrorText>}
            </Flex>
          )}

          {needsRedirectUri && (
            <CallbackUrlField
              registration={register("redirectUri")}
              value={watch("redirectUri") ?? ""}
              error={errors.redirectUri?.message}
              hint={
                selfServe?.defaultRedirectUri
                  ? `${provider.name} always uses this callback URL, so you shouldn't need to change it.`
                  : "Where your tool receives the sign-in response. Your tool's MCP or OAuth settings will show it."
              }
            />
          )}

          {needsToolName && <AuthMethodField value={authMethod} onChange={setAuthMethod} />}

          {!needsRedirectUri && (
            <Text description>
              {provider.name} only gives you a callback URL once the connection exists, so add it
              here afterwards.
            </Text>
          )}

          <Flex justifyContent="flex-end" gap={8}>
            <Button type="button" buttonStyle="inverse" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} loading={isLoading}>
              Generate
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
}
